// src/components/RequisitionBuilder.jsx
import { AddIcon, ArrowDownIcon, ArrowUpIcon, CopyIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    Badge, Box, Button, Divider, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader,
    DrawerOverlay, Flex, FormControl, FormErrorMessage, FormLabel, HStack, IconButton, Input,
    NumberInput, NumberInputField, Select, Switch, Tab, TabList, TabPanel, TabPanels, Tabs,
    Text, Textarea, VStack
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';

// Supported field types
const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'select', label: 'Select (single)' },
    { value: 'multiselect', label: 'Select (multi)' },
    { value: 'datalist', label: 'Datalist (with free text)' },
    { value: 'file', label: 'File Upload' },
    { value: 'display', label: 'Read-only Display' },
];

const emptyField = (type = 'text') => ({
    key: '',
    label: '',
    type,
    required: false,
    placeholder: '',
    helpText: '',
    options: [],        // for select/multiselect/datalist
    min: undefined,     // for number
    max: undefined,     // for number
    accept: undefined,  // for file
    maxFiles: undefined,// for file
    valueFrom: undefined,// for display binding
    default: undefined, // default value
    showIf: undefined,  // { field, equals }
});

// slug → KEY suggestion
const keyFromLabel = (s = '') =>
    String(s).trim()
        .replace(/[^\w]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase();

const uniqueKey = (proposed, allKeys) => {
    let base = proposed || 'field';
    if (!allKeys.includes(base)) return base;
    let i = 2;
    while (allKeys.includes(`${base}_${i}`)) i++;
    return `${base}_${i}`;
};

const parseOptionsTextarea = (txt) => String(txt || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);

const optionsToTextarea = (arr) => (arr || []).join('\n');

// Simple preview of the form (no validation here; it’s just visual)
const FieldPreview = ({ f }) => {
    const common = (
        <VStack align="stretch" spacing={1} mb={3}>
            <Text fontWeight="semibold">
                {f.label || '(no label)'}{' '}
                {f.required ? <Badge ml={2} colorScheme="red">required</Badge> : null}
                {f.showIf ? <Badge ml={2}>conditional</Badge> : null}
            </Text>
            {f.helpText ? <Text fontSize="xs" color="gray.500">{f.helpText}</Text> : null}
        </VStack>
    );

    switch (f.type) {
        case 'text': return <Box>{common}<Input placeholder={f.placeholder || ''} /></Box>;
        case 'number': return (
            <Box>{common}
                <NumberInput><NumberInputField placeholder={f.placeholder || ''} /></NumberInput>
                {(f.min != null || f.max != null) && <Text fontSize="xs" color="gray.500">Range: {f.min ?? '—'} to {f.max ?? '—'}</Text>}
            </Box>
        );
        case 'textarea': return <Box>{common}<Textarea rows={3} placeholder={f.placeholder || ''} /></Box>;
        case 'checkbox': return <Box>{common}<Switch isChecked={!!f.default} /></Box>;
        case 'datetime': return <Box>{common}<Input type="datetime-local" /></Box>;
        case 'select': return (
            <Box>{common}
                <Select placeholder="Select...">{(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}</Select>
            </Box>
        );
        case 'multiselect': return (
            <Box>{common}
                <Select multiple>{(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}</Select>
                <Text fontSize="xs" color="gray.500">Hold Ctrl/Cmd to select multiple (preview)</Text>
            </Box>
        );
        case 'datalist': return (
            <Box>{common}
                <Input list={`dl-${f.key || 'temp'}`} placeholder={f.placeholder || ''} />
                <datalist id={`dl-${f.key || 'temp'}`}>{(f.options || []).map(o => <option key={o} value={o} />)}</datalist>
            </Box>
        );
        case 'file': return (
            <Box>{common}
                <Input type="file" multiple={!!f.maxFiles && f.maxFiles > 1} />
                <Text fontSize="xs" color="gray.500">Accept: {(f.accept || []).join(', ') || 'any'} • Max files: {f.maxFiles || 1}</Text>
            </Box>
        );
        case 'display': return (
            <Box>{common}
                <Text fontSize="sm" color="gray.600">Displays from: <code>{f.valueFrom || '—'}</code></Text>
            </Box>
        );
        default: return <Box>{common}<Input placeholder={f.placeholder || ''} /></Box>;
    }
};

export default function TestRequisitionBuilder({
    isOpen,
    onClose,
    initialSchema,
    onSave,         // (schemaObject) => void
}) {
    // Normalize incoming schema
    const initial = useMemo(() => {
        if (!initialSchema) {
            return { schemaVersion: 1, title: 'REQUISITION', fields: [] };
        }
        try {
            // allow string or object
            const obj = typeof initialSchema === 'string' ? JSON.parse(initialSchema) : initialSchema;
            return {
                schemaVersion: obj.schemaVersion || 1,
                title: obj.title || 'REQUISITION',
                fields: Array.isArray(obj.fields) ? obj.fields : [],
            };
        } catch {
            return { schemaVersion: 1, title: 'REQUISITION', fields: [] };
        }
    }, [initialSchema]);

    const [title, setTitle] = useState(initial.title);
    const [schemaVersion, setSchemaVersion] = useState(initial.schemaVersion);
    const [fields, setFields] = useState(initial.fields);
    const [selected, setSelected] = useState(-1);
    const [jsonError, setJsonError] = useState('');

    const allKeys = useMemo(() => fields.map(f => f.key), [fields]);

    const addField = (type) => {
        setFields(prev => {
            const f = emptyField(type);
            f.label = FIELD_TYPES.find(t => t.value === type)?.label || 'Field';
            f.key = uniqueKey(keyFromLabel(f.label), prev.map(x => x.key));
            return [...prev, f];
        });
        setSelected(fields.length); // focus new
    };

    const duplicateField = (index) => {
        setFields(prev => {
            const copy = JSON.parse(JSON.stringify(prev[index]));
            copy.key = uniqueKey(copy.key, prev.map(x => x.key));
            const arr = [...prev];
            arr.splice(index + 1, 0, copy);
            return arr;
        });
        setSelected(index + 1);
    };

    const deleteField = (index) => {
        setFields(prev => prev.filter((_, i) => i !== index));
        setSelected(-1);
    };

    const move = (index, dir) => {
        setFields(prev => {
            const arr = [...prev];
            const j = index + dir;
            if (j < 0 || j >= arr.length) return arr;
            [arr[index], arr[j]] = [arr[j], arr[index]];
            return arr;
        });
        setSelected(i => Math.min(fields.length - 1, Math.max(0, i + dir)));
    };

    const updateField = (index, patch) => {
        setFields(prev => prev.map((f, i) => i === index ? { ...f, ...patch } : f));
    };

    const importJSON = (txt) => {
        try {
            const obj = JSON.parse(txt);
            setJsonError('');
            setTitle(obj.title || 'REQUISITION');
            setSchemaVersion(obj.schemaVersion || 1);
            setFields(Array.isArray(obj.fields) ? obj.fields : []);
            setSelected(-1);
        } catch (e) {
            setJsonError(e.message);
        }
    };

    const exportSchema = () => ({
        schemaVersion: Number(schemaVersion) || 1,
        title: title?.trim() || 'REQUISITION',
        fields,
    });

    const handleSave = () => {
        const schemaObj = exportSchema();
        onSave?.(schemaObj);
        onClose?.();
    };

    const selectedField = fields[selected];

    return (
        <Drawer isOpen={isOpen} onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader>Requisition Builder</DrawerHeader>
                <DrawerBody>
                    <Tabs size="sm" variant="enclosed">
                        <TabList>
                            <Tab>Fields</Tab>
                            <Tab>Preview</Tab>
                            <Tab>Import / Export</Tab>
                        </TabList>

                        <TabPanels>
                            {/* -------- Fields Tab -------- */}
                            <TabPanel>
                                <Flex gap={6} align="start" direction={{ base: 'column', md: 'row' }}>
                                    {/* Left: field list */}
                                    <Box flex="0 0 340px" borderWidth="1px" rounded="md" p={3}>
                                        <FormControl mb={3}>
                                            <FormLabel m={0}>Title</FormLabel>
                                            <Input value={title} onChange={e => setTitle(e.target.value)} />
                                        </FormControl>
                                        <FormControl mb={4}>
                                            <FormLabel m={0}>Schema Version</FormLabel>
                                            <NumberInput value={schemaVersion} min={1} onChange={(_, v) => setSchemaVersion(v || 1)}>
                                                <NumberInputField />
                                            </NumberInput>
                                        </FormControl>

                                        <VStack align="stretch" spacing={2} maxH="50vh" overflow="auto">
                                            {fields.map((f, i) => (
                                                <Box key={f.key || i} p={2} borderWidth="1px" rounded="md" bg={i === selected ? 'blue.50' : 'white'}>
                                                    <HStack justify="space-between">
                                                        <Button size="sm" variant="ghost" leftIcon={<EditIcon />} onClick={() => setSelected(i)}>
                                                            {f.label || f.key || '(unnamed)'}
                                                        </Button>
                                                        <HStack>
                                                            <IconButton size="xs" aria-label="up" icon={<ArrowUpIcon />} onClick={() => move(i, -1)} />
                                                            <IconButton size="xs" aria-label="down" icon={<ArrowDownIcon />} onClick={() => move(i, +1)} />
                                                            <IconButton size="xs" aria-label="dup" icon={<CopyIcon />} onClick={() => duplicateField(i)} />
                                                            <IconButton size="xs" aria-label="del" icon={<DeleteIcon />} onClick={() => deleteField(i)} />
                                                        </HStack>
                                                    </HStack>
                                                    <Text fontSize="xs" color="gray.500">{f.type}</Text>
                                                </Box>
                                            ))}
                                            {!fields.length && <Text color="gray.500" textAlign="center" py={6}>No fields yet</Text>}
                                        </VStack>

                                        <Divider my={3} />
                                        <HStack wrap="wrap" gap={2}>
                                            {FIELD_TYPES.map(t => (
                                                <Button key={t.value} size="sm" leftIcon={<AddIcon />} onClick={() => addField(t.value)}>
                                                    {t.label}
                                                </Button>
                                            ))}
                                        </HStack>
                                    </Box>

                                    {/* Right: editor */}
                                    <Box flex="1" borderWidth="1px" rounded="md" p={3} minH="420px">
                                        {selectedField ? (
                                            <VStack align="stretch" spacing={3}>
                                                <HStack>
                                                    <FormControl>
                                                        <FormLabel m={0}>Type</FormLabel>
                                                        <Select
                                                            value={selectedField.type}
                                                            onChange={(e) => updateField(selected, { type: e.target.value })}
                                                        >
                                                            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                        </Select>
                                                    </FormControl>
                                                    <FormControl>
                                                        <FormLabel m={0}>Key</FormLabel>
                                                        <Input
                                                            value={selectedField.key}
                                                            onChange={(e) => {
                                                                const proposed = keyFromLabel(e.target.value);
                                                                const finalKey = allKeys.filter((_, i) => i !== selected).includes(proposed)
                                                                    ? uniqueKey(proposed, allKeys.filter((_, i) => i !== selected))
                                                                    : proposed;
                                                                updateField(selected, { key: finalKey });
                                                            }}
                                                            placeholder="unique_key"
                                                        />
                                                    </FormControl>
                                                    <FormControl>
                                                        <FormLabel m={0}>Label</FormLabel>
                                                        <Input
                                                            value={selectedField.label}
                                                            onChange={(e) => {
                                                                const label = e.target.value;
                                                                // If key empty, derive from label
                                                                if (!selectedField.key) {
                                                                    const proposed = keyFromLabel(label);
                                                                    const finalKey = uniqueKey(proposed, allKeys.filter((_, i) => i !== selected));
                                                                    updateField(selected, { label, key: finalKey });
                                                                } else {
                                                                    updateField(selected, { label });
                                                                }
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormControl display="flex" alignItems="center" w="140px">
                                                        <FormLabel m={0} mr={3}>Required</FormLabel>
                                                        <Switch
                                                            isChecked={!!selectedField.required}
                                                            onChange={e => updateField(selected, { required: e.target.checked })}
                                                        />
                                                    </FormControl>
                                                </HStack>

                                                {/* Type-specific props */}
                                                {(selectedField.type === 'text' || selectedField.type === 'textarea' || selectedField.type === 'number') && (
                                                    <HStack>
                                                        <FormControl>
                                                            <FormLabel m={0}>Placeholder</FormLabel>
                                                            <Input
                                                                value={selectedField.placeholder || ''}
                                                                onChange={e => updateField(selected, { placeholder: e.target.value })}
                                                            />
                                                        </FormControl>
                                                        {selectedField.type === 'number' && (
                                                            <>
                                                                <FormControl w="160px">
                                                                    <FormLabel m={0}>Min</FormLabel>
                                                                    <NumberInput value={selectedField.min ?? ''} onChange={(_, v) => updateField(selected, { min: Number.isFinite(v) ? v : undefined })}>
                                                                        <NumberInputField />
                                                                    </NumberInput>
                                                                </FormControl>
                                                                <FormControl w="160px">
                                                                    <FormLabel m={0}>Max</FormLabel>
                                                                    <NumberInput value={selectedField.max ?? ''} onChange={(_, v) => updateField(selected, { max: Number.isFinite(v) ? v : undefined })}>
                                                                        <NumberInputField />
                                                                    </NumberInput>
                                                                </FormControl>
                                                            </>
                                                        )}
                                                    </HStack>
                                                )}

                                                {(selectedField.type === 'select' || selectedField.type === 'multiselect' || selectedField.type === 'datalist') && (
                                                    <FormControl>
                                                        <FormLabel m={0}>Options (one per line)</FormLabel>
                                                        <Textarea
                                                            rows={6}
                                                            value={optionsToTextarea(selectedField.options)}
                                                            onChange={(e) => updateField(selected, { options: parseOptionsTextarea(e.target.value) })}
                                                        />
                                                        <Text fontSize="xs" color="gray.500" mt={1}>
                                                            {selectedField.type === 'datalist' ? 'User can still type custom values.' : 'User must pick from the list.'}
                                                        </Text>
                                                    </FormControl>
                                                )}

                                                {selectedField.type === 'file' && (
                                                    <HStack>
                                                        <FormControl>
                                                            <FormLabel m={0}>Accept (comma separated MIME)</FormLabel>
                                                            <Input
                                                                placeholder="image/*, application/pdf"
                                                                value={Array.isArray(selectedField.accept) ? selectedField.accept.join(', ') : (selectedField.accept || '')}
                                                                onChange={(e) => updateField(selected, { accept: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                                                            />
                                                        </FormControl>
                                                        <FormControl w="200px">
                                                            <FormLabel m={0}>Max Files</FormLabel>
                                                            <NumberInput min={1} value={selectedField.maxFiles ?? 1} onChange={(_, v) => updateField(selected, { maxFiles: v || 1 })}>
                                                                <NumberInputField />
                                                            </NumberInput>
                                                        </FormControl>
                                                    </HStack>
                                                )}

                                                {selectedField.type === 'display' && (
                                                    <FormControl>
                                                        <FormLabel m={0}>Value From (binding)</FormLabel>
                                                        <Input
                                                            placeholder="e.g., test.code or order.patient.name"
                                                            value={selectedField.valueFrom || ''}
                                                            onChange={e => updateField(selected, { valueFrom: e.target.value })}
                                                        />
                                                    </FormControl>
                                                )}

                                                {/* Common props */}
                                                <FormControl>
                                                    <FormLabel m={0}>Help Text (optional)</FormLabel>
                                                    <Input
                                                        value={selectedField.helpText || ''}
                                                        onChange={e => updateField(selected, { helpText: e.target.value })}
                                                    />
                                                </FormControl>

                                                {/* Conditional visibility */}
                                                <Box borderWidth="1px" rounded="md" p={3}>
                                                    <Text fontWeight="semibold" mb={2}>Show this field only if…</Text>
                                                    <HStack>
                                                        <Select
                                                            placeholder="(no condition)"
                                                            value={selectedField.showIf?.field || ''}
                                                            onChange={(e) => {
                                                                const fld = e.target.value;
                                                                if (!fld) return updateField(selected, { showIf: undefined });
                                                                const equals = selectedField.showIf?.equals ?? '';
                                                                updateField(selected, { showIf: { field: fld, equals } });
                                                            }}
                                                        >
                                                            <option value="">(no condition)</option>
                                                            {fields
                                                                .filter((_, i) => i !== selected)
                                                                .map(f => <option key={f.key} value={f.key}>{f.label || f.key}</option>)}
                                                        </Select>
                                                        <Input
                                                            placeholder="equals value…"
                                                            value={selectedField.showIf?.equals ?? ''}
                                                            onChange={(e) => {
                                                                const fld = selectedField.showIf?.field;
                                                                if (!fld) return;
                                                                updateField(selected, { showIf: { field: fld, equals: e.target.value } });
                                                            }}
                                                        />
                                                    </HStack>
                                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                                        For checkbox, use <code>true</code> / <code>false</code>. For select, use one of the option values.
                                                    </Text>
                                                </Box>
                                            </VStack>
                                        ) : (
                                            <Flex align="center" justify="center" h="full"><Text color="gray.500">Select a field to edit…</Text></Flex>
                                        )}
                                    </Box>
                                </Flex>
                            </TabPanel>

                            {/* -------- Preview Tab -------- */}
                            <TabPanel>
                                <Box maxW="720px">
                                    <Text fontWeight="bold" mb={3}>{title || 'REQUISITION'}</Text>
                                    {fields.length === 0 && <Text color="gray.500">No fields. Add some in the Fields tab.</Text>}
                                    <VStack align="stretch">
                                        {fields.map(f => <FieldPreview key={f.key} f={f} />)}
                                    </VStack>
                                </Box>
                            </TabPanel>

                            {/* -------- Import / Export Tab -------- */}
                            <TabPanel>
                                <Text fontWeight="bold" mb={2}>Export</Text>
                                <Textarea
                                    isReadOnly
                                    rows={10}
                                    value={JSON.stringify({ schemaVersion, title, fields }, null, 2)}
                                />
                                <Divider my={4} />
                                <Text fontWeight="bold" mb={2}>Import (paste JSON)</Text>
                                <Textarea
                                    rows={8}
                                    placeholder='{"schemaVersion":1,"title":"...","fields":[...]}'
                                    onChange={(e) => importJSON(e.target.value)}
                                />
                                {jsonError && <FormErrorMessage>{jsonError}</FormErrorMessage>}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </DrawerBody>

                <DrawerFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleSave}>Use This Schema</Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
